import { TableSort } from "../../../hooks/useTableViewConfig";
import { ALL_COLUMNS, ColumnDef } from "./columns";
import { EnrichedRow } from "./derive";

const STATUS_SORT_ORDER = [
  "ATRASADO",
  "INICIO_PENDIENTE",
  "ADELANTADO",
  "EN_RITMO",
  "PENDIENTE_INICIO",
  "SIN_FECHAS",
  "FINALIZADA",
];

/**
 * Value a column sorts by: its own sortValue, else the CSV value (numbers as
 * numbers, "" as missing). Dates sort by their ISO value, status by urgency.
 */
export const sortKeyOf = (
  col: ColumnDef,
  row: EnrichedRow
): number | string | null => {
  if (col.id === "startDate") return row.startDate;
  if (col.id === "endDate") return row.endDate;
  if (col.id === "status") return STATUS_SORT_ORDER.indexOf(row.status);
  const v = col.csvValue(row);
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (v === "") return null;
  return /^-?\d+(\.\d+)?$/.test(v) ? parseFloat(v) : v;
};

/**
 * Registry columns in the user's order. Columns the saved order doesn't know
 * (added later) go right after their registry predecessor. `columns` is the
 * registry the user may see (without the admin-only ones for everybody else).
 */
export const orderColumns = (
  order: string[],
  columns: ColumnDef[] = ALL_COLUMNS
): ColumnDef[] => {
  const byId = new Map(columns.map((c) => [c.id, c]));
  const result = order.filter((id) => byId.has(id));
  columns.forEach((col, index) => {
    if (result.includes(col.id)) return;
    const predecessor = columns
      .slice(0, index)
      .reverse()
      .find((c) => result.includes(c.id));
    const at = predecessor ? result.indexOf(predecessor.id) + 1 : 0;
    result.splice(at, 0, col.id);
  });
  return result.map((id) => byId.get(id)!);
};

/** Rows sorted by a column; rows without a value go last either way. */
export function sortRows(
  rows: EnrichedRow[],
  sort: TableSort | null,
  columns: ColumnDef[] = ALL_COLUMNS
): EnrichedRow[] {
  const col = sort ? columns.find((c) => c.id === sort.columnId) : null;
  if (!sort || !col) return rows;
  const factor = sort.direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = sortKeyOf(col, a);
    const vb = sortKeyOf(col, b);
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    if (typeof va === "number" && typeof vb === "number") {
      return (va - vb) * factor;
    }
    return String(va).localeCompare(String(vb), "es") * factor;
  });
}
